import { HStack, Stack } from "@chakra-ui/layout";
import {
  Box,
  Button,
  Heading,
  Link,
  Step,
  StepIcon,
  StepIndicator,
  StepNumber,
  Stepper,
  StepSeparator,
  StepStatus,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import { Card } from "@src/components/ui-kit/Card";
import { CopyIcon } from "@src/components/ui-kit/HiddenCopyIcon";
import { TextField } from "@src/components/ui-kit/TextField";
import { formatDate } from "@src/utils/formatters";
import { useStore } from "@src/utils/hooks";
import { observer } from "mobx-react-lite";
import type { ReactNode } from "react";

const MCP_URL = "https://mcp.allfred.io/mcp";

function McpStep({ children }: { readonly children: ReactNode }) {
  return (
    <Step>
      <StepIndicator>
        <StepStatus
          complete={<StepIcon />}
          incomplete={<StepNumber />}
          active={<StepNumber />}
        />
      </StepIndicator>

      <Box flex="1" pb="4">
        {children}
      </Box>

      <StepSeparator />
    </Step>
  );
}

export const McpSettings = observer(function McpSettings() {
  const { editUserModalStore: store } = useStore();
  const tokens = store.user?.mcpTokens ?? [];

  return (
    <Stack data-testid="mcp-settings" spacing="6">
      <Text color="grey.600" fontSize="sm">
        <Trans>
          Connect Allfred to Claude, ChatGPT or another MCP client, so you can
          work with your projects, tasks, time entries, budgets and invoices
          directly from a chat. The connection runs under your own account — the
          client sees exactly what you see in Allfred, nothing more.
        </Trans>
      </Text>

      <Card data-testid="mcp-settings-help" p="5" shadow="none">
        <Text
          mb="4"
          color="grey.500"
          fontSize="xs"
          fontWeight="semibold"
          letterSpacing="wider"
          textTransform="uppercase"
        >
          <Trans>How to connect</Trans>
        </Text>

        <Stepper
          colorScheme="violet"
          gap="0"
          index={-1}
          orientation="vertical"
          size="sm"
          w="full"
        >
          <McpStep>
            <Text color="grey.600" fontSize="sm">
              <Trans>
                In your MCP client, add a custom connector pointing at{" "}
                <Link href={MCP_URL} isExternal>
                  {MCP_URL}
                </Link>
                . In Claude: Settings → Connectors → Add custom connector.
              </Trans>
            </Text>

            <HStack align="center" spacing="2" mt="2">
              <HStack
                data-testid="mcp-settings-copy-url"
                w="fit-content"
                px="3"
                py="1"
                bg="grey.50"
                rounded="md"
                spacing="2"
              >
                <Text color="grey.600" fontSize="sm">
                  {MCP_URL}
                </Text>
              </HStack>

              <CopyIcon
                copyIconName="copy-02"
                tooltipLabel={t`Copy URL`}
                valueToCopy={MCP_URL}
              />
            </HStack>
          </McpStep>

          <McpStep>
            <Text color="grey.600" fontSize="sm">
              <Trans>
                The client opens an Allfred page and shows you a pairing code.
              </Trans>
            </Text>
          </McpStep>

          <McpStep>
            <Text color="grey.800" fontSize="sm">
              <Trans>Paste that code below and confirm.</Trans>
            </Text>

            <HStack mt="2" spacing="2">
              <Box w="56">
                <TextField
                  id="field-user-mcp-token"
                  data-testid="mcp-settings-token-input"
                  placeholder={t`Enter pairing code`}
                  value={store.mcpToken}
                  onChange={store.setMcpToken}
                  type="text"
                />
              </Box>

              <Button
                data-testid="mcp-settings-token-add"
                colorScheme="violet"
                flexShrink={0}
                isDisabled={!store.mcpToken.trim()}
                isLoading={store.isMcpSubmitting}
                onClick={store.connectToMcp}
              >
                <Trans>Confirm</Trans>
              </Button>
            </HStack>
          </McpStep>

          <McpStep>
            <Text color="grey.600" fontSize="sm">
              <Trans>
                Go back to the screen your client opened and press{" "}
                <Text as="span" fontWeight="semibold">
                  Check & Authorize
                </Text>
                . Done — the connector is live.
              </Trans>
            </Text>
          </McpStep>
        </Stepper>
      </Card>

      <Stack data-testid="mcp-settings-tokens" spacing="3">
        <Heading as="h3" size="sm">
          <Trans>Connected clients</Trans>
        </Heading>

        <Table size="sm" variant="simple">
          <Thead>
            <Tr>
              <Th>
                <Trans>Name</Trans>
              </Th>

              <Th>
                <Trans>Connected at</Trans>
              </Th>

              <Th>
                <Trans>Actions</Trans>
              </Th>
            </Tr>
          </Thead>

          <Tbody>
            {tokens.length === 0 ? (
              <Tr data-testid="mcp-settings-tokens-empty">
                <Td colSpan={3}>
                  <Trans>No connections yet</Trans>
                </Td>
              </Tr>
            ) : (
              tokens.map((token) => (
                <Tr key={token.id} data-testid="mcp-settings-token">
                  <Td>{token.mcp_name?.trim() || t`MCP client`}</Td>

                  <Td>{formatDate(new Date(token.created_at))}</Td>

                  <Td>
                    <Button
                      data-testid="mcp-settings-token-disconnect"
                      fontSize="sm"
                      isLoading={store.isMcpSubmitting}
                      onClick={() => store.disconnectFromMcp(token.id)}
                      variant="link"
                    >
                      <Trans>Disconnect</Trans>
                    </Button>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Stack>
    </Stack>
  );
});
